import AxiosConfig from './LensAxiosConfig'
import firebase from '../config/FirebaseConfig'

const getLensMetrics = async (lensId = null, registryOverride) => {
  let reqData
  if (!registryOverride) {
    // Pull the current token off the user
    const { Aa: token } = firebase.auth()?.currentUser || {}

    const url = lensId ? `/metrics-registry/v2/${lensId}?status=1` : '/metrics-registry/v2?status=1'
    const { data } = await AxiosConfig.get(
      url,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    reqData = data
  } else {
    reqData = { data: { meta: registryOverride, tiktok: registryOverride } }
  }

  const { meta: rawMetaData, tiktok: rawTiktokData } = reqData.data

  const parsedMetaData = parseRawMetricData(rawMetaData)
  const parsedTiktokData = parseRawMetricData(rawTiktokData)

  return {
    meta: parsedMetaData,
    tiktok: parsedTiktokData
  }
}

const parseRawMetricData = (rawData) => {
  // Parse and format the data
  const start = performance.now()

  // We parse the raw data into three different structures:

  // 1. metrics: This is the hierarchical structure for rendering metrics in the column selector drawer.
  const metrics = {}
  // 2. metricLookup: The same format as the raw registry array, but as an object with the key as the index.
  //    This is used to lookup metadata (e.g., name, type, etc.) for a metric given its key
  const metricLookup = {}
  // 3. metricGroups: An object with the parent group as the key, and an array of associated metrics as the value.
  //    This is used for rendering the metrics in the filter selector
  const metricGroups = {}

  rawData.forEach((item) => {
    // --- Set metric lookup ---
    metricLookup[item.key] = item

    // --- Set metric groups ---
    const {
      parent_group: parentGroup,
      child_group: childGroup,
      table_group: tableGroup,
      col_order: colOrder,
      table_col: tableCol,
      table_row_name: tableRowName
    } = item

    if (!metricGroups[parentGroup]) {
      metricGroups[parentGroup] = [item]
    } else {
      metricGroups[parentGroup].push(item)
    }

    // --- Set metrics ---
    if (!metrics[parentGroup]) { // Ensure parent group exists, if not, define it
      metrics[parentGroup] = {
        metrics: [],
        subSections: [],
        tableSections: {} // Object here because we use table groups as the keys during the first pass through
      }
    }
    if (!tableGroup) { // If the metric is not rendered in a table, we place it into its parent group, or child group if it has one 
      // Child group
      if (childGroup) {
        const currentSub = metrics[parentGroup].subSections.find((sec) => sec.sectionId === childGroup)
        if (!currentSub) {
          metrics[parentGroup].subSections = [{ sectionId: childGroup, domId: childGroup, metrics: [item] }]
        } else {
          currentSub.metrics.push(item)
        }
      } else {
        metrics[parentGroup].metrics.push(item)
      }
    }
    // Otherwise, sort the metric into its table section, row, and column (or define any of these if they are missing)
    else {
      const currentTable = metrics[parentGroup].tableSections[tableGroup]
      if (!currentTable) {
        const row = []
        const colHeaders = []
        row[colOrder - 1] = item
        colHeaders[colOrder - 1] = tableCol
        metrics[parentGroup].tableSections[tableGroup] = {
          // domId: tableGroup,
          metrics: { [tableRowName]: row },
          colHeaders
        }
      } else {
        if (!currentTable.metrics?.[tableRowName]) {
          const row = []
          row[colOrder - 1] = item
          currentTable.metrics[tableRowName] = row
        } else {
          currentTable.colHeaders[colOrder - 1] = tableCol
          currentTable.metrics[tableRowName][colOrder - 1] = item
        }
      }
    }
  })

  // Final cleanup pass for table sections - This iteration fills in any blanks for rows in table sections with missing/null columns
  Object.keys(metrics).forEach((key) => {
    const sectionData = metrics[key]
    if (!_.isEmpty(sectionData.tableSections)) {
      const tableSubs = Object.entries(sectionData.tableSections).map(([tblSec, sectionData]) => {
        const formattedMetrics = Object.entries(sectionData.metrics).map(([row, metrics]) => ({
          rowName: row,
          metrics
        }))

        // If a table row is missing a metric that is expected by the colHeaders, add a null metric in its place (represented by disabled checkbox)
        const colHeaders = [...sectionData.colHeaders]
        const normalizedMetrics = formattedMetrics.reduce((acc, row) => {
          const metrics = [...row.metrics].filter(metric => metric)
          colHeaders.forEach((header, index) => {
            if (!metrics.some(metric => metric.table_col === header)) {
              metrics.splice(index, 0, {
                key: `null_${row.rowName.replace(/\s+/g, '_')}_${header}`,
                is_null: true
              })
            }
          })
          acc.push({ ...row, metrics })
          return acc
        }, [])

        return {
          domId: tblSec,
          sectionId: tblSec,
          colHeaders: sectionData.colHeaders,
          metrics: normalizedMetrics
        }
      })
      metrics[key].tableSections = tableSubs
    } else {
      metrics[key].tableSections = [] // Fallback to an empty array for sections without table data
    }
  })
  const end = performance.now()

  console.log(`%c Metric (Query + Format) execution time: ${end - start} ms`, 'color: green; font-size: 14px;')

  return {
    rawData,
    metrics,
    metricLookup,
    metricGroups
  }
}

export default {
  getLensMetrics
}
